import { ServerConfig } from "@/config/config";
import { LedgerAccount } from "@/models/ledger-account";
import LedgerAccountExtraFields from "@/models/ledger-account-extra-fields";
import { View } from "@/models/view";
import { Axios } from "@/utils/axios";
import { advancedSearch } from "@/utils/browse/browse";
import { IGetResponse } from "@/utils/get-response";
import { IExtraFieldQuickEditViaMenuDataResult } from "@/utils/models/extra-fields";
import { getModel } from "@/utils/models/model";
import { IQueryParamsWithOptionalView } from "@/utils/query-params";
import { FetchedTotal } from "@/utils/views/fetched-total";

export class LedgerAccountServiceClass {
	public readonly url:string = `${ServerConfig.host}/ledger-account`;
	private ledgerAccountCache:LedgerAccount[] = [];

	private updateCache(account:LedgerAccount) {
		let i = this.ledgerAccountCache.findIndex(l=>l.Code == account.Code);
		if (i == -1){
			this.ledgerAccountCache.push(account);
		}else{
			this.ledgerAccountCache[i] = account;
		}
	}

	public async fetchLedgerAccounts():Promise<LedgerAccount[]> {
		if (this.ledgerAccountCache.length != 0) {
			return this.ledgerAccountCache.map(l=>new LedgerAccount(l.getJSON()));
		}
		let result = await Axios.get(this.url);
		this.ledgerAccountCache = result.data.map((l:any)=>new LedgerAccount(l));
		return result.data.map((l:any)=>new LedgerAccount(l));
	}

	public async getLedgerAccounts(query: IQueryParamsWithOptionalView): Promise<IGetResponse<LedgerAccount>> {
		let result = await advancedSearch(query, this.url, getModel("LedgerAccount"));
		let records = result.data.records as number;
		let contacts = (result.data.data || []).map((c: any) => new LedgerAccount(c));
		return {data: contacts, records};
	}

	async getTotals(view:View):Promise<FetchedTotal[]>{
		let result = await Axios.get(`${this.url}/view/${view.ID}/totals`);
		return (result.data || []).map((d:any)=>new FetchedTotal(d));
	}

	public async getLedgerAccount(code:string):Promise<LedgerAccount>{
		let result = await Axios.get(`${this.url}/${code}`);
		return new LedgerAccount(result.data);
	}

	public async createLedgerAccount(account:LedgerAccount):Promise<LedgerAccount> {
		let result = await Axios.post(this.url, account.getJSON());
		this.updateCache(new LedgerAccount(result.data));
		return new LedgerAccount(result.data);
	}

	public async update(account:LedgerAccount):Promise<void> {
		let response = await Axios.put(`${this.url}/${account.Code}`, account.getJSON());
		let result = new LedgerAccount(response.data);
		this.updateCache(result);
		for (let a of this.ledgerAccountCache) {
			if (a.GroupCode == account.Code) {
				a.Description = result.Description;
			}
		}
	}

	public async deleteLedgerAccount(account:LedgerAccount):Promise<void>{
		await Axios.delete(`${this.url}/${account.Code}`);
		let i = this.ledgerAccountCache.findIndex(l=>l.Code == account.Code);
		if (i != -1) {
			this.ledgerAccountCache.splice(i, 1);
		}
	}

	public async importCsv(file:File):Promise<void> {
		let form = new FormData();
		form.append("csv", file);
		await Axios.post(`${this.url}/import-csv`, form);
		this.ledgerAccountCache.splice(0, this.ledgerAccountCache.length);
		await this.fetchLedgerAccounts();
	}

	public clearCache(){
		this.ledgerAccountCache = [];
	}

	public async setDefaultExtraFieldValues():Promise<void>{
		await Axios.post(`${this.url}/set-default-extra-field-values`);
	}


	public async batchEditExtraField(account:LedgerAccount[], field:string, value:IExtraFieldQuickEditViaMenuDataResult) {
		let body = {
			Codes: account.map(s=>s.Code),
			Field: field,
			NumValue: value.numValue,
			TextValue: value.textValue,
			BoolValue: value.boolValue,
			TimeValue: value.timeValue
		};
		await Axios.post(`${this.url}/batch-edit-extra-field`, body);
	}

	public async updateExtraFields(account:LedgerAccount, extraFields:LedgerAccountExtraFields):Promise<LedgerAccountExtraFields>{
		let result = await Axios.put(`${this.url}/extra-fields/${account.Code}`, extraFields.getJSON());
		return new LedgerAccountExtraFields(result.data);
	}

	public async getExtraFields(account:LedgerAccount):Promise<LedgerAccountExtraFields>{
		let result = await this.getLedgerAccount(account.Code);
		return result.ExtraFields || new LedgerAccountExtraFields();
	}
}

export const LedgerAccountService = new LedgerAccountServiceClass();